/**
 * Fetch Users Feedback UseCase
 * Flutter source: lib/presentation/screens/feedback/users_feedback_screen.dart:86-90
 *   (`FeedbackRepository().getUsersFeedback(_lastFetchedItem, AppConstants.feedbackListPagination)`).
 *
 * Fetches one page of users' in-app feedback, newest-first. Thin forwarding use
 * case: defaults the page limit to the shared `DEFAULT_LIMIT`
 * (value 9, matching Flutter `feedbackListPagination = 9`, lib/domain/constants/app_constants.dart:47)
 * and forwards `cursor`/`limit` to `FeedbackService.getUsersFeedback`. Any
 * dedup / pagination accumulation lives in the hook (Task 7), mirroring Flutter
 * where the screen owns that state — see users_feedback_screen.dart:91-93.
 *
 * Web parity reference: fetchNotificationsUseCase.ts (thin forwarding use case
 * with a default-limit constant).
 */
import { FeedbackService } from '@/domain/services/feedback/feedbackService';
import type { InAppFeedbackEntity } from '@/domain/entities/feedback/inAppFeedbackEntity';
import type { PageCursor } from '@/data/types/json';
import { SERVER_FETCH_CONSTANTS } from '@/domain/constants/serverFetchConstants';

export class FetchUsersFeedbackUseCase {
  private feedbackService: FeedbackService;

  constructor(feedbackService: FeedbackService = FeedbackService.getInstance()) {
    this.feedbackService = feedbackService;
  }

  async execute(
    cursor?: PageCursor,
    limit: number = SERVER_FETCH_CONSTANTS.DEFAULT_LIMIT,
  ): Promise<{ items: InAppFeedbackEntity[]; hasMore: boolean; cursor?: PageCursor }> {
    return this.feedbackService.getUsersFeedback(cursor, limit);
  }
}

export const fetchUsersFeedbackUseCase = new FetchUsersFeedbackUseCase();
